import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

const EMPTY_VALUE = '-';

class ProductComparisonComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.products = this.#readProducts();
    this.#enableDebugTools();
    this.selectors = Array.isArray(this.refs.selectors) ? this.refs.selectors : [];
    this.selectedVariantByColumn = new Map();

    this.selectors.forEach((selector) => {
      selector.addEventListener('change', this.#handleSelectorChange);
    });

    this.addEventListener('click', this.#handleVariantOptionClick);
    this.addEventListener('click', this.#handleBuyClick);

    this.#renderInitialColumns();
    this.#updateDisabledOptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.selectors?.forEach((selector) => {
      selector.removeEventListener('change', this.#handleSelectorChange);
    });
    this.removeEventListener('click', this.#handleVariantOptionClick);
    this.removeEventListener('click', this.#handleBuyClick);
  }

  #readProducts() {
    const source = this.refs.productData;
    if (!(source instanceof HTMLScriptElement)) return [];

    try {
      const data = JSON.parse(source.textContent || '{}');
      const products = Array.isArray(data) ? data : data.products || [];

      return Array.isArray(products) ? products.filter((product) => product?.id) : [];
    } catch (error) {
      console.warn('Product comparison data could not be parsed.', error);
      return [];
    }
  }

  #enableDebugTools() {
    window.ShokzProductComparisonDebug = () => this.#debugProducts();

    if (new URLSearchParams(window.location.search).has('debugProductComparison') || this.dataset.debugProducts === 'true') {
      this.#debugProducts();
    }
  }

  #debugProducts() {
    const payload = this.products.map(({ id, title, url, price, optionValues, specs }) => ({
      id,
      title,
      url,
      price,
      optionValues,
      specs
    }));

    console.info('Product comparison products', payload);
    return payload;
  }

  #handleBuyClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-buy-link], [data-mini-link]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    // If href is '#' there's no product — let it do nothing
    if (!link.href || link.getAttribute('href') === '#') {
      event.preventDefault();
      return;
    }

    // Find which column this button belongs to
    const card = link.closest('[data-column-card], [data-column-mini-card]');
    const column = card ? Number(card.dataset.column || 0) : 0;

    const selector = this.selectors.find((s) => Number(s.dataset.column || 0) === column);
    const product = selector ? this.#getProduct(selector.value) : null;
    if (!product) return;

    const selectedVariant = this.#getSelectedVariant(column, product);
    const variantId = selectedVariant?.id || product.selectedVariantId;
    if (!variantId) return;

    // Block if unavailable
    if (selectedVariant && !selectedVariant.available) {
      event.preventDefault();
      return;
    }

    // Prevent navigation
    event.preventDefault();

    // Visual feedback — loading
    const originalText = link.textContent;
    link.textContent = '…';
    link.style.pointerEvents = 'none';
    link.style.opacity = '0.7';

    const cartUrl = window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/add.js`
      : '/cart/add.js';

    fetch(cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          // Error from Shopify
          link.textContent = data.message || 'Error';
          setTimeout(() => {
            link.textContent = originalText;
            link.style.pointerEvents = '';
            link.style.opacity = '';
          }, 2000);
          return;
        }

        link.textContent = '✓ Agregado';
        document.dispatchEvent(
          new CartAddEvent(data, String(variantId), {
            source: 'product-comparison-component',
            productId: String(product.id),
            variantId: String(variantId),
            itemCount: 1,
          })
        );

        setTimeout(() => {
          link.textContent = originalText;
          link.style.pointerEvents = '';
          link.style.opacity = '';
        }, 2000);
      })
      .catch(() => {
        link.textContent = originalText;
        link.style.pointerEvents = '';
        link.style.opacity = '';
      });
  };

  #handleSelectorChange = (event) => {
    const selector = event.currentTarget;
    if (!(selector instanceof HTMLSelectElement)) return;

    const column = Number(selector.dataset.column || 0);
    const product = this.#getProduct(selector.value);

    this.selectedVariantByColumn.delete(column);
    this.#renderColumn(column, product);
    this.#updateDisabledOptions();
  };

  #handleVariantOptionClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-variant-option]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const card = button.closest('[data-column-card]');
    if (!(card instanceof HTMLElement)) return;

    const column = Number(card.dataset.column || 0);
    const selector = this.selectors.find((item) => Number(item.dataset.column || 0) === column);
    const product = selector ? this.#getProduct(selector.value) : null;
    if (!product) return;

    const optionPosition = Number(button.dataset.optionPosition || 0);
    const optionValue = button.dataset.optionValue || '';
    if (!optionPosition || !optionValue) return;

    const selectedVariant = this.#getSelectedVariant(column, product);
    const selectedOptions = [...(selectedVariant?.options || product.variants?.[0]?.options || [])];
    selectedOptions[optionPosition - 1] = optionValue;

    const nextVariant =
      this.#getVariantForOptionSelection(product, selectedOptions, true) ||
      this.#getVariantForOptionSelection(product, selectedOptions, false);

    if (!nextVariant) return;

    this.selectedVariantByColumn.set(column, String(nextVariant.id));
    this.#renderColumn(column, product);
  };

  #getProduct(id) {
    return this.products.find((product) => String(product.id) === String(id)) || null;
  }

  #renderColumn(column, product) {
    if (!product) {
      const card = this.querySelector(`[data-column-card][data-column="${column}"]`);
      if (card) {
        this.#replaceImage(card.querySelector('[data-product-image-wrap]'), null, 'product-comparison__image');
        this.#setText(card.querySelector('[data-product-badge]'), '\u00a0');
        this.#setText(card.querySelector('[data-product-title]'), 'Selecciona un producto');
        this.#setText(card.querySelector('[data-product-subtitle]'), '\u00a0');
        this.#renderVariantOptions(card.querySelector('[data-variant-options]'), null, null);
        this.#renderSku(card.querySelector('[data-product-sku]'), null);
        this.#renderPrice(card.querySelector('[data-product-price]'), null, null);
        this.#setHref(card.querySelector('[data-buy-link]'), '#');
        this.#setHref(card.querySelector('[data-learn-link]'), '#');
        this.#updateBuyButton(card.querySelector('[data-buy-link]'), null);
      }

      this.#replaceImage(this.querySelector(`[data-quick-image-wrap][data-column="${column}"]`), null, '');

      this.querySelectorAll(`[data-spec][data-column="${column}"]`).forEach((cell) => {
        this.#setText(cell, EMPTY_VALUE);
      });

      this.#renderMiniCard(column, null, null);
      return;
    }

    const selectedVariant = this.#getSelectedVariant(column, product);
    const productImage = selectedVariant?.image?.src ? selectedVariant.image : product.image;
    const productUrl = this.#getVariantUrl(product, selectedVariant);

    const card = this.querySelector(`[data-column-card][data-column="${column}"]`);
    if (card) {
      this.#replaceImage(card.querySelector('[data-product-image-wrap]'), productImage, 'product-comparison__image');
      this.#setText(card.querySelector('[data-product-badge]'), product.badge || '\u00a0');
      this.#setText(card.querySelector('[data-product-title]'), product.title || EMPTY_VALUE);
      this.#setText(card.querySelector('[data-product-subtitle]'), product.subtitle || '\u00a0');
      this.#renderVariantOptions(card.querySelector('[data-variant-options]'), product, selectedVariant);
      this.#renderSku(card.querySelector('[data-product-sku]'), selectedVariant);
      this.#renderPrice(card.querySelector('[data-product-price]'), product, selectedVariant);
      this.#setHref(card.querySelector('[data-buy-link]'), productUrl);
      this.#setHref(card.querySelector('[data-learn-link]'), productUrl);
      this.#updateBuyButton(card.querySelector('[data-buy-link]'), selectedVariant);
    }

    this.#replaceImage(
      this.querySelector(`[data-quick-image-wrap][data-column="${column}"]`),
      product.quickLookImage,
      ''
    );

    this.querySelectorAll(`[data-spec][data-column="${column}"]`).forEach((cell) => {
      const key = cell.getAttribute('data-spec') || '';
      this.#setText(cell, this.#normalizeSpecValue(product.specs?.[key]));
    });

    this.#renderMiniCard(column, product, selectedVariant);
  }

  #renderInitialColumns() {
    this.selectors.forEach((selector) => {
      const column = Number(selector.dataset.column || 0);
      const product = this.#getProduct(selector.value);
      this.#renderColumn(column, product);
    });
  }

  #renderMiniCard(column, product, selectedVariant = null) {
    const card = this.querySelector(`[data-column-mini-card][data-column="${column}"]`);
    if (!card) return;

    if (!product) {
      this.#replaceImage(card.querySelector('[data-mini-image-wrap]'), null, '');
      this.#setText(card.querySelector('[data-mini-title]'), 'Selecciona un producto');
      this.#setText(card.querySelector('[data-mini-price]'), EMPTY_VALUE);
      this.#setHref(card.querySelector('[data-mini-link]'), '#');
      return;
    }

    const productImage = selectedVariant?.image?.src ? selectedVariant.image : product.image;

    this.#replaceImage(card.querySelector('[data-mini-image-wrap]'), productImage, '');
    this.#setText(card.querySelector('[data-mini-title]'), product.title || EMPTY_VALUE);
    this.#setText(card.querySelector('[data-mini-price]'), selectedVariant?.price || product.price || EMPTY_VALUE);
    this.#setHref(card.querySelector('[data-mini-link]'), this.#getVariantUrl(product, selectedVariant));
  }

  #renderVariantOptions(container, product, selectedVariant) {
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren();

    if (!product) {
      container.hidden = true;
      return;
    }

    const allOptions = product.optionValues || [];
    const selectableOptions = allOptions.filter((option) => (option.values || []).length > 1);

    if (product.hasOnlyDefaultVariant || !selectableOptions.length) {
      container.hidden = true;
      return;
    }

    container.hidden = false;

    // Map of option position → currently active value.
    // Falls back to the first value of that option when no variant is selected,
    // ensuring testSelection always has the correct length for variant matching.
    const activeByPosition = new Map();
    allOptions.forEach((opt) => {
      const pos = Number(opt.position || 0);
      if (!pos) return;
      const fromVariant = selectedVariant?.options?.[pos - 1];
      const fallback = String(opt.values?.[0]?.name || '');
      activeByPosition.set(pos, fromVariant || fallback);
    });

    selectableOptions.forEach((option) => {
      const isColor = this.#isColorOption(option.name);
      const optPos = Number(option.position || 0);

      // Group wrapper
      const group = document.createElement('div');
      group.className = 'product-comparison__option-group';

      // Label
      const label = document.createElement('p');
      label.className = 'product-comparison__option-label';
      label.textContent = option.name;
      group.append(label);

      // Chips row
      const row = document.createElement('div');
      row.className = 'product-comparison__option-chips';

      (option.values || []).forEach((value) => {
        const optionValue = typeof value === 'string' ? { name: value } : value;
        const valueName = String(optionValue.name || '');
        if (!valueName || !optPos) return;

        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = isColor
          ? 'product-comparison__chip product-comparison__swatch'
          : 'product-comparison__chip';
        chip.title = valueName;
        chip.dataset.variantOption = '';
        chip.dataset.optionPosition = String(optPos);
        chip.dataset.optionValue = valueName;

        if (isColor) {
          chip.setAttribute('aria-label', `${option.name}: ${valueName}`);
          chip.style.setProperty('--swatch-color', this.#swatchColor(optionValue));
        } else {
          chip.textContent = valueName;
        }

        // Active state
        const activeVal = activeByPosition.get(optPos) || '';
        const isActive = this.#normalizeOptionName(activeVal) === this.#normalizeOptionName(valueName);
        chip.classList.toggle('is-active', isActive);
        chip.setAttribute('aria-pressed', String(isActive));

        // Availability: build a full-length selection array using ALL option positions.
        // This guarantees testSelection.length === variant.options.length so the
        // variant lookup never fails due to an array length mismatch.
        const testSelection = allOptions.map((opt) => {
          const p = Number(opt.position || 0);
          return p === optPos ? valueName : (activeByPosition.get(p) || '');
        });
        const matchingVariant = this.#getVariantForOptionSelection(product, testSelection, false);
        chip.dataset.optionAvailable = String(Boolean(matchingVariant?.available));

        row.append(chip);
      });

      group.append(row);
      container.append(group);
    });
  }

  #updateBuyButton(button, selectedVariant) {
    if (!(button instanceof HTMLAnchorElement)) return;
    const available = selectedVariant == null ? true : Boolean(selectedVariant.available);
    button.textContent = available ? 'Agregar al carrito' : 'No disponible';
    button.classList.toggle('is-unavailable', !available);
    button.setAttribute('aria-disabled', String(!available));
  }

  #renderPrice(container, product, selectedVariant = null) {
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren();

    if (!product && !selectedVariant) {
      const empty = document.createElement('span');
      empty.className = 'price';
      empty.textContent = EMPTY_VALUE;
      container.append(empty);
      return;
    }

    const priceSource = selectedVariant || product;

    if (priceSource.compareAtPrice) {
      const compare = document.createElement('span');
      compare.className = 'compare-at-price';
      compare.textContent = priceSource.compareAtPrice;
      container.append(compare, ' ');
    }

    const price = document.createElement('span');
    price.className = 'price';
    price.textContent = priceSource.price || EMPTY_VALUE;
    container.append(price);
  }

  #renderSku(element, selectedVariant) {
    if (!(element instanceof HTMLElement)) return;

    const sku = String(selectedVariant?.sku || '').trim();
    element.hidden = !sku;
    element.textContent = sku ? `SKU: ${sku}` : '';
  }

  #replaceImage(container, image, className) {
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren();

    if (!image?.src) {
      const placeholder = document.createElement('div');
      placeholder.className = className || 'product-comparison__quick-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      container.append(placeholder);
      return;
    }

    const img = document.createElement('img');
    img.src = image.src;
    img.alt = image.alt || '';
    img.loading = 'lazy';
    if (className) img.className = className;
    container.append(img);
  }

  #updateDisabledOptions() {
    const selectedValues = this.selectors.map((selector) => selector.value).filter(Boolean);

    this.selectors.forEach((selector) => {
      Array.from(selector.options).forEach((option) => {
        option.disabled = option.value !== selector.value && selectedValues.includes(option.value);
      });
    });
  }

  #setText(element, value) {
    if (element) element.textContent = value;
  }

  #setHref(element, value) {
    if (element instanceof HTMLAnchorElement) element.href = value || '#';
  }

  #normalizeSpecValue(value) {
    if (Array.isArray(value)) {
      const items = value.map((item) => String(item ?? '').trim()).filter(Boolean);
      return items.length ? items.join(', ') : EMPTY_VALUE;
    }

    if (value === null || value === undefined) return EMPTY_VALUE;

    const normalized = String(value).trim();
    return normalized || EMPTY_VALUE;
  }

  #getSelectedVariant(column, product) {
    if (!product) return null;

    const variants = product.variants || [];
    if (!variants.length) return null;

    const storedVariantId = this.selectedVariantByColumn.get(column);
    const selectedVariant =
      variants.find((variant) => String(variant.id) === String(storedVariantId)) ||
      variants.find((variant) => String(variant.id) === String(product.selectedVariantId)) ||
      variants.find((variant) => variant.available) ||
      variants[0];

    if (selectedVariant) this.selectedVariantByColumn.set(column, String(selectedVariant.id));

    return selectedVariant || null;
  }

  #getVariantForOptionSelection(product, selectedOptions, availableOnly = true) {
    return (product.variants || []).find((variant) => {
      if (availableOnly && !variant.available) return false;
      if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) return false;

      return variant.options.every((option, index) => {
        return this.#normalizeOptionName(option) === this.#normalizeOptionName(selectedOptions[index]);
      });
    });
  }

  #getVariantUrl(product, selectedVariant) {
    if (!product?.url) return '#';
    if (!selectedVariant?.id) return product.url;
    if (selectedVariant.url) return selectedVariant.url;

    const separator = product.url.includes('?') ? '&' : '?';
    return `${product.url}${separator}variant=${selectedVariant.id}`;
  }

  #isColorOption(name) {
    const normalized = this.#normalizeOptionName(name);
    return normalized.includes('color') || normalized.includes('colour');
  }

  #normalizeOptionName(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  #swatchColor(value) {
    const swatchImage = String(value?.swatchImage || '').trim();
    if (swatchImage) return `url("${swatchImage}")`;

    const swatchColor = String(value?.swatchColor || '').trim();
    if (swatchColor) return swatchColor;

    const valueName = String(value?.name || value || '');
    const cssVar = String(value?.swatchCssVar || '').trim();
    const normalized = this.#normalizeOptionName(valueName);
    const knownColors = {
      black: '#111111',
      negro: '#111111',
      blanco: '#f5f5f5',
      white: '#f5f5f5',
      gray: '#8a8a8a',
      gris: '#8a8a8a',
      grey: '#8a8a8a',
      blue: '#2d5f9a',
      azul: '#2d5f9a',
      red: '#b73535',
      rojo: '#b73535',
      pink: '#d88faa',
      rosa: '#d88faa',
      beige: '#d4b98e',
      orange: '#d96b2b',
      naranja: '#d96b2b',
      green: '#467a4d',
      verde: '#467a4d'
    };
    const fallback = knownColors[normalized] || normalized.replace(/[^a-z0-9#(),.% -]/g, '');

    if (cssVar) return `var(${cssVar}, ${fallback || 'rgb(var(--color-foreground-rgb) / 0.14)'})`;

    return fallback || 'rgb(var(--color-foreground-rgb) / 0.14)';
  }
}

if (!customElements.get('product-comparison-component')) {
  customElements.define('product-comparison-component', ProductComparisonComponent);
}
